// src/data/projectWeeklyClient.js
//
// PMO Slice B — Weekly Overview client boundary.
//   GET  /api/project-weekly/weeks
//   GET  /api/project-weekly/overview?weekDate=YYYY-MM-DD
//   POST /api/project-weekly/projects/:id/notes
//   POST /api/project-weekly/projects/:id/tracks/:trackKey/lines/:lineId/corrections
//   PUT  /api/projects/:id/tracks/:trackKey/status
//   PUT  /api/projects/:id/weekly-summary
//   POST /api/project-weekly/freeze
//
// Server error messages surface verbatim via errorFromResponse (body.error).

import { NetworkError, ValidationError, errorFromResponse } from "./errors.js";
import { PROJECT_TRACK_KEYS, OWNERSHIP_SLOTS } from "../lib/projectTracks.js";
import { invalidateCache } from "./projectsClient.js";
import { sharedWeeklyRead } from "../services/projectWeeklyReads.js";

export const WEEK_RE = /^\d{4}-\d{2}-\d{2}$/;
export const TRACK_STATUSES = Object.freeze(["on_track", "watch", "blocked"]);
export const OVERALL_STATUSES = Object.freeze(["on_track", "at_risk", "critical"]);
export const CORRECTION_ACTIONS = Object.freeze(["replace", "strike"]);

export function isMondayIso(iso) {
  if (typeof iso !== "string" || !WEEK_RE.test(iso)) return false;
  const d = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso && d.getUTCDay() === 1;
}

function week(weekDate) {
  if (!isMondayIso(weekDate)) throw new ValidationError("weekDate must be a calendar Monday (YYYY-MM-DD)");
  return weekDate;
}

function seg(value, name) {
  if (typeof value !== "string" || !value) throw new ValidationError(`${name} is required`);
  return encodeURIComponent(value);
}

async function request(method, url, body, { signal } = {}) {
  let res;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new NetworkError(`Network error: ${err?.message || err}`);
  }
  if (!res.ok) {
    const error = await errorFromResponse(res);
    // Preserve known HTTP denials (including freeze's 422) separately from
    // requests with no acknowledgement and potentially committed appends.
    error.status = res.status;
    throw error;
  }
  const data = res.status === 204 ? null : await res.json();
  if (method !== "GET") invalidateCache();
  return data;
}

export function getWeeklyWeeks(opts) {
  return sharedWeeklyRead("weeks", null, () => request("GET", "/api/project-weekly/weeks"), opts);
}

export function getWeeklyOverview(weekDate, opts) {
  const query = weekDate ? `?weekDate=${encodeURIComponent(week(weekDate))}` : "";
  return sharedWeeklyRead("overview", weekDate, () => request("GET", `/api/project-weekly/overview${query}`), opts);
}

export function getWeeklyRail(weekDate, opts) {
  const query = weekDate ? `?weekDate=${encodeURIComponent(week(weekDate))}` : "";
  return sharedWeeklyRead("rail", weekDate, async () => {
    const data = await request("GET", `/api/project-weekly/rail${query}`);
    if (!isMondayIso(data?.weekDate) || !Array.isArray(data?.projects)) throw new Error("Could not load projects for this week.");
    return data;
  }, opts);
}

export function savePortfolioSummary({ weekDate, narrative }) {
  if (typeof narrative !== "string" || !narrative.trim() || narrative.length > 10000) {
    throw new ValidationError("Portfolio summary must contain 1–10000 characters");
  }
  return request("PUT", "/api/project-weekly/portfolio-summary", { weekDate: week(weekDate), narrative: narrative.trim() });
}

export function postWeeklyNote(projectId, { weekDate, note }) {
  const text = typeof note === "string" ? note.trim() : "";
  if (!text) throw new ValidationError("Note cannot be empty");
  return request("POST", `/api/project-weekly/projects/${seg(projectId, "projectId")}/notes`, { weekDate: week(weekDate), note: text });
}

export function postLineCorrection(projectId, trackKey, lineId, { weekDate, revision, action, replacement }) {
  if (!TRACK_KEYS.includes(trackKey)) throw new ValidationError("Invalid track key");
  if (!CORRECTION_ACTIONS.includes(action)) throw new ValidationError("Invalid correction action");
  if (!Number.isInteger(revision)) throw new ValidationError("revision is required");
  const body = { weekDate: week(weekDate), revision, action };
  if (action === "replace") {
    const text = typeof replacement === "string" ? replacement.trim() : "";
    if (!text) throw new ValidationError("Replacement text cannot be empty");
    body.replacement = text;
  }
  return request(
    "POST",
    `/api/project-weekly/projects/${seg(projectId, "projectId")}/tracks/${seg(trackKey, "trackKey")}/lines/${seg(String(lineId ?? ""), "lineId")}/corrections`,
    body,
  );
}

export function putTrackStatus(projectId, trackKey, { weekDate, status }) {
  if (!TRACK_KEYS.includes(trackKey)) throw new ValidationError("Invalid track key");
  if (!TRACK_STATUSES.includes(status)) throw new ValidationError("Invalid track status");
  return request("PUT", `/api/projects/${seg(projectId, "projectId")}/tracks/${seg(trackKey, "trackKey")}/status`, { weekDate: week(weekDate), status });
}

export function postTrackResponse(projectId, trackKey, { weekDate, response }) {
  if (!TRACK_KEYS.includes(trackKey)) throw new ValidationError("Invalid track key");
  if (!["quiet", "reporting"].includes(response)) throw new ValidationError("Invalid track response");
  return request("POST", `/api/project-weekly/projects/${seg(projectId, "projectId")}/tracks/${seg(trackKey, "trackKey")}/response`,
    { weekDate: week(weekDate), response });
}

export function putWeeklySummary(projectId, { weekDate, statusOverall, narrative }) {
  if (!OVERALL_STATUSES.includes(statusOverall)) throw new ValidationError("Invalid overall status");
  return request("PUT", `/api/projects/${seg(projectId, "projectId")}/weekly-summary`, {
    weekDate: week(weekDate), statusOverall, narrative: typeof narrative === "string" ? narrative.trim() : "",
  });
}

// PATCH /api/projects/:id/weekly-entry - one section per request.
export const WEEKLY_ENTRY_SECTIONS = Object.freeze({
  overall: ["statusOverall", "narrative"],
  thisWeek: ["accomplishments", "upcoming", "blockers"],
  pmo: ["pmoNote"],
});
export function patchWeeklyEntry(projectId, { weekDate, ...fields }) {
  const keys = Object.keys(fields).filter((k) => fields[k] !== undefined);
  if (keys.length === 0) throw new ValidationError("No weekly entry fields to save");
  const section = Object.values(WEEKLY_ENTRY_SECTIONS).find((list) => keys.every((k) => list.includes(k)));
  if (!section) throw new ValidationError("Weekly entry patches must stay within one section");
  if (fields.statusOverall !== undefined && !OVERALL_STATUSES.includes(fields.statusOverall)) throw new ValidationError("Invalid overall status");
  const body = { weekDate: week(weekDate) };
  for (const k of keys) body[k] = k === "statusOverall" ? fields[k] : (typeof fields[k] === "string" ? fields[k].trim() : "");
  return request("PATCH", `/api/projects/${seg(projectId, "projectId")}/weekly-entry`, body);
}

// PATCH /api/projects/:id/weekly-financials - editable finance fields only.
export function patchWeeklyFinancials(projectId, { weekDate, approvedBudget, forecastAtCompletion }) {
  const body = { weekDate: week(weekDate) };
  for (const [k, v] of Object.entries({ approvedBudget, forecastAtCompletion })) {
    if (v === undefined) continue;
    if (v !== null && !Number.isFinite(v)) throw new ValidationError(`${k} must be a number`);
    body[k] = v;
  }
  if (Object.keys(body).length === 1) throw new ValidationError("No financial fields to save");
  return request("PATCH", `/api/projects/${seg(projectId, "projectId")}/weekly-financials`, body);
}

export function freezeWeeklyWeek(weekDate) {
  return request("POST", "/api/project-weekly/freeze", { weekDate: week(weekDate) });
}

// ─── PMO Slice C — contributor card + People & roles matrix ──────────────────
//   GET    /api/my/assignments
//   GET    /api/projects/:id/participants
//   PUT    /api/projects/:id/participants
//   DELETE /api/projects/:id/participants/:participantId   -> {ok:true}
//   GET    /api/people            (admin full; owner-limited fields)
//   GET    /api/admin/portfolio-roles
//   PUT    /api/admin/portfolio-roles   (per-person replacement)
//   GET    /api/projects/:id/week?weekDate=
//   POST   /api/projects/:id/tracks/:trackKey/updates
//   POST   /api/projects/:id/updates/complete

export const TRACK_KEYS = PROJECT_TRACK_KEYS;
export const DELIVERY_ROLES = Object.freeze(["delivery_director", "delivery_owner"]);

function trackKey(value) {
  if (!TRACK_KEYS.includes(value)) throw new ValidationError("Invalid track key");
  return value;
}

export function getMyAssignments(opts) {
  return request("GET", "/api/my/assignments", null, opts);
}

export function getProjectParticipants(projectId, opts) {
  return request("GET", `/api/projects/${seg(projectId, "projectId")}/participants`, null, opts);
}

export function putProjectParticipant(projectId, { personId, trackKey: key, ownershipSlot, isPrimary }) {
  const tk = key === null || key === undefined ? null : trackKey(key);
  const slot = tk === null ? (ownershipSlot || "delivery_owner") : null;
  if ((slot && !OWNERSHIP_SLOTS.some(s => s.key === slot)) || (tk !== null && ownershipSlot != null)) throw new ValidationError("Invalid ownership slot");
  if (typeof personId !== "string" || !personId) throw new ValidationError("personId is required");
  if (typeof isPrimary !== "boolean") throw new ValidationError("isPrimary must be true or false");
  return request("PUT", `/api/projects/${seg(projectId, "projectId")}/participants`, {
    personId, trackKey: tk, ownershipSlot: slot, roleOnProject: tk === null ? "owner" : "contributor", isPrimary,
  });
}

export function deleteProjectParticipant(projectId, participantId) {
  return request("DELETE", `/api/projects/${seg(projectId, "projectId")}/participants/${seg(String(participantId ?? ""), "participantId")}`);
}

export function getPeopleDirectory(opts) {
  return request("GET", "/api/people", null, opts);
}

export function getPortfolioRoles(opts) {
  return request("GET", "/api/admin/portfolio-roles", null, opts);
}

export function putPortfolioRoles(personId, roles) {
  if (typeof personId !== "string" || !personId) throw new ValidationError("personId is required");
  if (!Array.isArray(roles) || roles.some((r) => !DELIVERY_ROLES.includes(r)) || new Set(roles).size !== roles.length) {
    throw new ValidationError("Invalid portfolio roles");
  }
  return request("PUT", "/api/admin/portfolio-roles", { personId, roles });
}

export function getProjectWeek(projectId, weekDate, opts) {
  return request("GET", `/api/projects/${seg(projectId, "projectId")}/week?weekDate=${encodeURIComponent(week(weekDate))}`, null, opts);
}

export function postTrackUpdate(projectId, key, { weekDate, note }) {
  const text = typeof note === "string" ? note.trim() : "";
  if (!text) throw new ValidationError("Note cannot be empty");
  return request("POST", `/api/projects/${seg(projectId, "projectId")}/tracks/${trackKey(key)}/updates`, { weekDate: week(weekDate), note: text });
}

export function postUpdateComplete(projectId, { weekDate }) {
  return request("POST", `/api/projects/${seg(projectId, "projectId")}/updates/complete`, { weekDate: week(weekDate) });
}
